import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Dropdown,
    DropdownItemProps,
    Icon,
    Modal,
    Radio,
    TransitionablePortal,
} from "semantic-ui-react";
import { toast } from "../../../../..";
import {
    AddJustification,
    RemoveJustification,
} from "../../../../../actions/justificationsActions";
import {
    SetGromOptimizerData,
    SetIncludeReportingInfoInIva,
    SetJustification,
    SetKeepSensitivitiesData,
    SetRaoInfUnbdFallback,
} from "../../../../../actions/raoReportActions";
import { IJustification } from "../../../../../models/justification";
import {
    addJustification,
    deleteJustification,
} from "../../../../../services/justificationsService";
import { AppState } from "../../../../../store/configureStore";

export const Justifications = () => {
    const dispatch = useDispatch();
    const user = useSelector((state: AppState) => state.user);
    const raoReport = useSelector((state: AppState) => state.raoReport);
    const justifications = useSelector((state: AppState) => state.justifications);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleOpenModal = (value: string) => {
        if (user.role !== "TSOAdmin") {
            toast("Not authorized to delete IVA justification", false)
            return;
        }
        setModalOpen(true);
        setSelectedValue(value);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedValue("");
        setLoading(false);
    };

    const handleAddJustification = async (value: string) => {
        try {
            if (user.role !== "TSOAdmin") {
                toast("Not authorized to add new IVA justification", false)
                return;
            }
            setLoading(true);
            const newJustification: IJustification = {
                guid: "",
                tso: user.guid ?? "",
                value: value,
            };
            const res = await addJustification(newJustification);
            if (res.ok) {
                const just: IJustification = await res.json();
                const justProp: DropdownItemProps = {
                    key: just.guid,
                    text: just.value,
                    value: just.value,
                };
                dispatch(AddJustification(justProp));
                dispatch(SetJustification(just.value as string));
                toast("New IVA justification successfully added", true);
            } else toast("Unable to add new IVA justification", false);
        } catch {
            toast("Unable to add new IVA justification", false);
        } finally {
            setLoading(false);
            setDropdownOpen(false);
        }
    };

    const handleDeleteJustification = async () => {
        try {
            if (user.role !== "TSOAdmin") {
                toast("Not authorized to delete IVA justification", false)
                return;
            }
            setLoading(true);
            const just = justifications.find(x => x.text === selectedValue)!;
            const res = await deleteJustification(just.key);
            if (res.ok) {
                dispatch(RemoveJustification(just));
                dispatch(SetJustification(justifications[0].text as string));
                toast("IVA justification successfully deleted", true);
            } else toast("Unable to delete IVA justification", false);
        } catch {
            toast("Unable to add delete justification", false);
        } finally {
            handleCloseModal();
        }
    };

    return (
        <section id="section-justification-a" className="reports-half">
            <p id="p-justification-IVAvalue">IVA value justification</p>
            <div id="div-justification-a" className="reports-field">
                <label id="label-justification-IVAvalue">Select IVA value justification</label>
                <Dropdown
                    id="dropdown-justification-a"
                    search
                    selection
                    selectOnBlur={false}
                    value={raoReport.justification}
                    text={raoReport.justification}
                    allowAdditions
                    onAddItem={(e, data: any) => {
                        handleAddJustification(data.value);
                        setDropdownOpen(false);
                    }}
                    closeOnBlur
                    closeOnChange
                    onBlur={() => setDropdownOpen(false)}
                    onChange={() => setDropdownOpen(false)}
                    onClose={() => setDropdownOpen(false)}
                    onClick={() => setDropdownOpen(true)}
                    open={dropdownOpen}
                    additionLabel="Custom Language: "
                >
                    <Dropdown.Menu id="dropdownMenu-justification-a" open={dropdownOpen}>
                        {justifications.map((j,index) => (
                            <Dropdown.Item 
                                id={`drodownItem-justification-a-${index}`}
                                key={j.key}
                                selected={j.value === raoReport.justification}
                                {...j}
                                onClick={(e, data: any) => {
                                    dispatch(SetJustification(data.value as string));
                                    setDropdownOpen(false);
                                }}
                            >
                                {j.text}
                                <Icon
                                    id={`icon-justification-a-${index}`}
                                    name="close"
                                    onClick={() => handleOpenModal(j.value as string)}
                                />
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <div id="div-justification-c" className="reports-field rao-fallback-container">
                <p id="p-justification-reportingInformation">Reporting Information in IVA file</p>
                <Radio
                    toggle
                    label="Include"
                    checked={raoReport.includeReportingInfoInIva}
                    onChange={() => dispatch(SetIncludeReportingInfoInIva())}
                />
            </div>
            <div id="div-justification-d" className="reports-field rao-fallback-container">
                <p id="p-justification-RAOfallback">RAO Inf/UnBd Fallback</p>
                <Radio
                    toggle
                    label="Enable fallback"
                    checked={raoReport.infUnBdFallback}
                    onChange={() => dispatch(SetRaoInfUnbdFallback())}
                />
            </div>
            {(user.role === "Admin" || user.role === "SuperAdmin") && (
                <div id="div-justification-e" className="reports-field rao-fallback-container">
                    <Radio
                        toggle
                        label="GROM_OPTIMIZER data"
                        checked={raoReport.gromOptimizerData}
                        onChange={() => dispatch(SetGromOptimizerData())}
                    />
                </div>
            )}
            {(user.role === "Admin" || user.role === "SuperAdmin") && (
                <div id="div-justification-f" className="reports-field rao-fallback-container" >
                    <Radio
                        toggle
                        label="Keep Sensitivities data"
                        checked={raoReport.keepSensitivitiesData}
                        onChange={() => dispatch(SetKeepSensitivitiesData())}
                    />
                </div>
            )}
            <TransitionablePortal
                id="transitionablePortal-justification-g"
                open={modalOpen}
                onOpen={() => setTimeout(() => document.body.classList.add("modal-fade-in"), 0)}
                transition={{ animation: "scale", duration: 300 }}
            >
                <Modal id="modal-justification-g" onClose={() => handleCloseModal()} open closeOnDimmerClick size="small">
                    <Modal.Header id="modalHeader-justification-delete">
                        Are you sure you want to delete this IVA value justification?
                    </Modal.Header>
                    <Modal.Actions id="modalActions-justification-g">
                        <Button
                            id="button-justification-no"
                            content="No"
                            onClick={() => handleCloseModal()}
                            disabled={loading}
                        />
                        <Button
                            id="button-justification-yes"
                            primary
                            content="Yes"
                            onClick={handleDeleteJustification}
                            disabled={loading}
                            loading={loading}
                        />
                    </Modal.Actions>
                </Modal>
            </TransitionablePortal>
        </section>
    );
};
